import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

// Terms of Service Modal Content
function TermsModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Terms of Service</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          <div className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p className="text-xs text-gray-500">Last updated: January 2026</p>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">1. Acceptance of Terms</h3>
              <p>By creating an account and using myTotTot ("the Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">2. Description of Service</h3>
              <p>myTotTot is a baby tracking application that allows parents and caregivers to record and monitor their baby's activities including feeding times, nappy changes, sleep patterns, and medical information. The Service uses end-to-end encryption to protect your data.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">3. User Responsibilities</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>You must be at least 18 years old to use this Service.</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You are responsible for remembering your password. Due to our zero-knowledge encryption, we cannot recover your data if you lose your password.</li>
                <li>You agree to provide accurate and complete information when creating your account.</li>
                <li>You are responsible for all activity that occurs under your account.</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">4. Data Ownership</h3>
              <p>You retain full ownership of all data you input into the Service. Your data is encrypted on your device before being stored on our servers. We cannot access your encrypted data without your encryption keys.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">5. Acceptable Use</h3>
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Use the Service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Upload malicious code or content</li>
                <li>Share your account credentials with others</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">6. Medical Disclaimer</h3>
              <p className="font-medium text-red-600 dark:text-red-400">IMPORTANT: myTotTot is not a medical device or service. The information stored and tracked in this app is for personal reference only and should not be used as a substitute for professional medical advice, diagnosis, or treatment. Always seek the advice of your paediatrician or other qualified health provider with any questions you may have regarding your child's health.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">7. Service Availability</h3>
              <p>We strive to provide reliable service but cannot guarantee 100% uptime. We reserve the right to modify, suspend, or discontinue the Service at any time with reasonable notice.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">8. Limitation of Liability</h3>
              <p>To the maximum extent permitted by law, myTotTot shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the Service.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">9. Changes to Terms</h3>
              <p>We may update these Terms of Service from time to time. We will notify you of any material changes by email or through the Service. Your continued use of the Service after changes constitutes acceptance of the new terms.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">10. Termination</h3>
              <p>You may terminate your account at any time through the Settings page. Upon termination, your encrypted data will be permanently deleted from our servers.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">11. Governing Law</h3>
              <p>These Terms shall be governed by and construed in accordance with the laws of the United Kingdom, without regard to its conflict of law provisions.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">12. Contact</h3>
              <p>If you have any questions about these Terms, please contact us through the Support page in the application.</p>
            </section>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button onClick={onClose} className="btn-primary w-full">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Privacy Policy Modal Content
function PrivacyModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Privacy Policy</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <span className="text-2xl">&times;</span>
            </button>
          </div>

          <div className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300 space-y-4">
            <p className="text-xs text-gray-500">Last updated: January 2026</p>

            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 dark:text-green-200 mb-2">Our Privacy Commitment</h3>
              <p className="text-green-700 dark:text-green-300">myTotTot is built with privacy at its core. We use <strong>zero-knowledge end-to-end encryption</strong>, which means your baby's data is encrypted on your device before it ever reaches our servers. We cannot read, access, or share your personal data - only you hold the keys.</p>
            </div>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">1. Information We Collect</h3>

              <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-3 mb-1">Account Information</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Email address</strong> - Used for account authentication and important notifications</li>
                <li><strong>Name</strong> - Used to personalise your experience</li>
                <li><strong>Password hash</strong> - We store only a secure hash of your password, never the password itself</li>
              </ul>

              <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-3 mb-1">Encrypted Data (We Cannot Access)</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>Baby profile information (name, date of birth, medical details)</li>
                <li>Event logs (feeding, nappy changes, sleep, etc.)</li>
                <li>Photos attached to events</li>
                <li>Notes and observations</li>
              </ul>
              <p className="mt-2 italic">All sensitive data is encrypted using AES-256-GCM encryption on your device. We store only the encrypted data - we do not have the keys to decrypt it.</p>

              <h4 className="font-medium text-gray-800 dark:text-gray-200 mt-3 mb-1">Technical Information</h4>
              <ul className="list-disc pl-5 space-y-1">
                <li>IP address (for security and fraud prevention)</li>
                <li>Device type and browser information</li>
                <li>Usage timestamps (when you log in, not what you do)</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">2. How We Use Your Information</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>To provide and maintain the Service</li>
                <li>To authenticate your identity</li>
                <li>To send essential service notifications (password resets, security alerts)</li>
                <li>To prevent fraud and abuse</li>
                <li>To improve the Service based on aggregated, anonymised usage patterns</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">3. Data Storage and Security</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Encryption at rest:</strong> All data stored on our servers is encrypted</li>
                <li><strong>Encryption in transit:</strong> All communications use TLS 1.3</li>
                <li><strong>End-to-end encryption:</strong> Your sensitive data is encrypted with keys only you control</li>
                <li><strong>Cloud storage:</strong> Photos are stored in Cloudflare R2 with client-side encryption</li>
                <li><strong>Data location:</strong> Data is stored in secure data centres within the European Economic Area</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">4. Data Sharing</h3>
              <p>We do not sell, rent, or share your personal information with third parties for marketing purposes. We may share data only in these limited circumstances:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>With your consent:</strong> When you explicitly choose to share data (e.g., with caregivers or healthcare providers)</li>
                <li><strong>Service providers:</strong> We use trusted infrastructure providers (database hosting, email delivery) who are contractually bound to protect your data</li>
                <li><strong>Legal requirements:</strong> If required by law, though your encrypted data would be unreadable</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">5. Your Rights (GDPR)</h3>
              <p>Under UK/EU data protection law, you have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Access:</strong> Request a copy of your data (available in Settings)</li>
                <li><strong>Rectification:</strong> Correct inaccurate data</li>
                <li><strong>Erasure:</strong> Delete your account and all associated data</li>
                <li><strong>Portability:</strong> Export your data in a machine-readable format</li>
                <li><strong>Restriction:</strong> Limit how we process your data</li>
                <li><strong>Objection:</strong> Object to certain types of processing</li>
              </ul>
              <p className="mt-2">To exercise these rights, please use the Settings page or contact us through Support.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">6. Data Retention</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your encrypted data is retained for as long as your account is active</li>
                <li>Upon account deletion, all data is permanently removed within 30 days</li>
                <li>Some anonymised, aggregated data may be retained for analytics</li>
                <li>Audit logs are retained for 12 months for security purposes</li>
              </ul>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">7. Cookies and Tracking</h3>
              <p>We use only essential cookies required for the Service to function:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>Authentication tokens:</strong> To keep you logged in</li>
                <li><strong>Security tokens:</strong> To prevent cross-site request forgery</li>
              </ul>
              <p className="mt-2">We do not use advertising cookies, tracking pixels, or third-party analytics that track individual users.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">8. Children's Privacy</h3>
              <p>While myTotTot is designed to track information about babies and young children, the Service is intended for use by parents and guardians who are at least 18 years old. We do not knowingly collect information directly from children.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">9. Changes to This Policy</h3>
              <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through the Service. The "Last updated" date at the top indicates when the policy was last revised.</p>
            </section>

            <section>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">10. Contact Us</h3>
              <p>If you have questions about this Privacy Policy or our data practices, please contact us through the Support page in the application.</p>
              <p className="mt-2">For data protection concerns, you also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">ico.org.uk</a>.</p>
            </section>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button onClick={onClose} className="btn-primary w-full">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [success, setSuccess] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  const { register, isLoading, error, clearError } = useAuth();

  const validatePassword = (pwd: string): string[] => {
    const errors: string[] = [];
    if (pwd.length < 12) errors.push('At least 12 characters');
    if (!/[A-Z]/.test(pwd)) errors.push('One uppercase letter');
    if (!/[a-z]/.test(pwd)) errors.push('One lowercase letter');
    if (!/[0-9]/.test(pwd)) errors.push('One number');
    if (!/[^A-Za-z0-9]/.test(pwd)) errors.push('One special character');
    return errors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    setValidationErrors([]);

    // Validation
    const pwdErrors = validatePassword(password);
    if (pwdErrors.length > 0) {
      setValidationErrors(pwdErrors);
      return;
    }

    if (password !== confirmPassword) {
      setValidationErrors(['Passwords do not match']);
      return;
    }

    if (!acceptTerms) {
      setValidationErrors(['You must accept the Terms of Service and Privacy Policy']);
      return;
    }

    try {
      await register(email, password, name);
      // Store redirect in localStorage so it survives the email verification flow
      if (redirectTo) {
        localStorage.setItem('postVerifyRedirect', redirectTo);
      }
      setSuccess(true);
    } catch {
      // Error is handled in the store
    }
  };

  if (success) {
    // Determine where to send user after registration
    const loginUrl = redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
        <div className="max-w-md w-full text-center">
          <div className="text-6xl mb-4">✉️</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Check your email
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            We've sent a verification link to <strong>{email}</strong>.
            Please click the link to verify your account.
          </p>
          {redirectTo && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              After verifying, you'll be able to accept the invite.
            </p>
          )}
          <Link to={loginUrl} className="btn-primary">
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 py-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo */}
        <div className="text-center">
          <span className="text-6xl">👶</span>
          <h1 className="mt-4 text-3xl font-bold text-gray-900 dark:text-white">
            Create Account
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Start tracking your little one's journey
          </p>
        </div>

        {/* Form */}
        <div className="card p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {(error || validationErrors.length > 0) && (
              <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-sm">
                {error || validationErrors.map((e, i) => (
                  <div key={i}>• {e}</div>
                ))}
              </div>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Your Name
              </label>
              <input
                id="name"
                type="text"
                required
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="Jane Doe"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="••••••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Password must be at least 12 characters with uppercase, lowercase, number, and special character.
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="••••••••••••"
              />
            </div>

            <div className="flex items-start">
              <input
                id="terms"
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-1 h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <label htmlFor="terms" className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                I have read and agree to the{' '}
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="text-primary-600 hover:underline font-medium"
                >
                  Terms of Service
                </button>{' '}
                and{' '}
                <button
                  type="button"
                  onClick={() => setShowPrivacyModal(true)}
                  className="text-primary-600 hover:underline font-medium"
                >
                  Privacy Policy
                </button>
              </label>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? (
                <span className="spinner mr-2" />
              ) : null}
              Create Account
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 font-medium"
            >
              Sign in
            </Link>
          </p>
        </div>

        {/* Security note */}
        <div className="text-center text-xs text-gray-500 dark:text-gray-500 space-y-1">
          <p>🔒 Your data is encrypted end-to-end</p>
          <p>We generate secure encryption keys during registration</p>
        </div>
      </div>

      {/* Modals */}
      {showTermsModal && <TermsModal onClose={() => setShowTermsModal(false)} />}
      {showPrivacyModal && <PrivacyModal onClose={() => setShowPrivacyModal(false)} />}
    </div>
  );
}
